import { useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import type { Ref } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { curriculumAccentBgClass } from './courseTheme'
import { LessonCard } from './LessonCard'
import type { Course, Lesson } from '@/pages/learner/ui-model/types'

export interface CourseAccordionProps {
  course: Course
  defaultOpen?: boolean
  emptyText?: string
  onLessonClick?: (lesson: Lesson) => void
  currentLessonId?: number | null
  currentLessonRef?: Ref<HTMLButtonElement>
}

export function CourseAccordion({
  course,
  defaultOpen = false,
  emptyText,
  onLessonClick,
  currentLessonId,
  currentLessonRef,
}: CourseAccordionProps) {
  const { t } = useTranslation('learner')
  const [open, setOpen] = useState(defaultOpen)
  const [openModuleId, setOpenModuleId] = useState<number | null | undefined>(undefined)
  const percent = course.totalLessons > 0
    ? Math.round((course.completedLessons / course.totalLessons) * 100)
    : 0

  const firstModuleId = course.modules[0]?.id ?? null
  const defaultModuleId = course.modules.find((module) =>
    module.lessons.some((lesson) => lesson.status === 'current'),
  )?.id ?? firstModuleId
  const selectedModuleStillVisible = course.modules.some(
    (module) => module.id === openModuleId,
  )
  const effectiveOpenModuleId = openModuleId === undefined
    ? defaultModuleId
    : openModuleId !== null && !selectedModuleStillVisible
      ? defaultModuleId
      : openModuleId

  const toggleModule = (moduleId: number) => {
    setOpenModuleId((selectedModuleId) => {
      const currentStillVisible = course.modules.some(
        (module) => module.id === selectedModuleId,
      )
      const effectiveCurrentId = selectedModuleId === undefined
        ? defaultModuleId
        : selectedModuleId !== null && !currentStillVisible
          ? defaultModuleId
          : selectedModuleId
      return effectiveCurrentId === moduleId ? null : moduleId
    })
  }

  return (
    <section className="rounded-[20px] bg-lc-bg-primary px-4 pb-4 pt-2.5 shadow-lc-v3-card">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 rounded-2xl p-4 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
      >
        <span className="flex min-w-0 items-center gap-4">
          <span
            className={`${curriculumAccentBgClass[course.accent]} flex size-10 shrink-0 items-center justify-center rounded-[10px] text-lc-v3-h5 font-bold text-lc-text-inverse`}
          >
            {course.badgeLabel}
          </span>
          <span className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
            <strong className="truncate text-lc-v3-h4 font-semibold text-lc-v3-gray-950">
              <ContentMetadataText text={course.title} />
            </strong>
            <span className="text-lc-v3-body-2 font-medium text-lc-text-tertiary">
              {t('library.accordion.courseSummary', {
                chapters: course.totalModules,
                lessons: course.totalLessons,
                completed: course.completedLessons,
              })}
            </span>
          </span>
        </span>

        <span className="flex shrink-0 items-center gap-4 sm:gap-6">
          <span className="flex flex-col items-end gap-1">
            <span className="text-lc-v3-body-2 font-bold text-lc-key-500">
              {t('library.accordion.progress', { percent })}
            </span>
            <span className="h-2 w-24 overflow-hidden rounded bg-lc-v3-gray-200 sm:w-40">
              <span
                className="block h-full rounded bg-lc-key-500"
                style={{ width: `${percent}%` }}
              />
            </span>
          </span>
          {open ? (
            <ChevronUp className="size-6 text-lc-text-secondary" aria-hidden />
          ) : (
            <ChevronDown className="size-6 text-lc-text-secondary" aria-hidden />
          )}
        </span>
      </button>

      {open ? (
        course.modules.length > 0 ? (
          <div className="rounded-2xl bg-lc-v3-gray-100 p-2">
            <div className="flex flex-col gap-4">
              {course.modules.map((module) => {
                const moduleOpen = effectiveOpenModuleId === module.id
                return (
                  <section
                    key={module.id}
                    className="overflow-hidden rounded-2xl border border-lc-v3-gray-300 bg-lc-bg-primary"
                  >
                    <button
                      type="button"
                      onClick={() => toggleModule(module.id)}
                      aria-expanded={moduleOpen}
                      className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-lc-key-500 sm:px-6"
                    >
                      <span className="flex min-w-0 flex-1 items-center gap-3">
                        <span className="w-12 shrink-0 text-lc-v3-body-1 font-bold text-lc-text-link">
                          {t('library.accordion.chapterLabel', { order: module.order })}
                        </span>
                        <strong className="truncate text-lc-v3-h5 font-semibold text-lc-v3-gray-950">
                          <ContentMetadataText text={module.title} />
                        </strong>
                      </span>
                      <span className="flex shrink-0 items-center gap-4 sm:gap-6">
                        <span className="hidden items-center gap-1 text-lc-v3-body-2 font-medium sm:flex">
                          <span className="text-lc-text-tertiary">
                            {t('library.accordion.lessonCount', { count: module.totalLessons })}
                          </span>
                          <span className="text-lc-v3-gray-600">·</span>
                          <span className={module.completedLessons > 0
                            ? 'text-lc-success-600'
                            : 'text-lc-text-tertiary'}
                          >
                            {t('library.accordion.completedCount', {
                              count: module.completedLessons,
                            })}
                          </span>
                        </span>
                        {moduleOpen ? (
                          <ChevronUp className="size-6 text-lc-text-secondary" aria-hidden />
                        ) : (
                          <ChevronDown className="size-6 text-lc-text-secondary" aria-hidden />
                        )}
                      </span>
                    </button>

                    {moduleOpen ? (
                      <div className="grid grid-cols-1 gap-4 px-4 pb-4 pt-2 lg:grid-cols-2">
                        {module.lessons.map((lesson) => (
                          <LessonCard
                            key={lesson.id}
                            lesson={lesson}
                            onClick={onLessonClick}
                            elementRef={lesson.id === currentLessonId ? currentLessonRef : undefined}
                          />
                        ))}
                      </div>
                    ) : null}
                  </section>
                )
              })}
            </div>
          </div>
        ) : (
          <div className="rounded-2xl bg-lc-v3-gray-100 px-4 py-12 text-center text-lc-v3-body-2 font-medium text-lc-text-tertiary">
            {emptyText ?? t('library.accordion.empty')}
          </div>
        )
      ) : null}
    </section>
  )
}
